<template>
  <div class="overflow-auto h-[70vh]">
    <v-data-table
      :headers="tableHeaders"
      :items="leavePlans"
      :loading="loading"
      loading-text="Loading leave plans..."
      class="border border-[#e0e0e0] table-radius mt-2"
      show-select
      disable-sort
    >
      <template v-for="(header, index) in tableHeaders" v-slot:[`header.${header.value}`]>
        <p :key="index" class="font-title" v-tooltip="{ text: header.text }">
          {{ header.text }}
        </p>
      </template>
      <template v-slot:[`item.planName`]="{ item }">
        <td class="px-4 py-3">{{ item.planName }}_{{ item.planYear }}</td>
      </template>

      <!-- <template v-slot:[`item.planYear`]="{ item }">
        <td class="px-4 py-3">{{ item.planYear }}</td>
      </template> -->

      <template v-slot:[`item.branchName`]="{ item }">
        <td class="px-4 py-3">{{ item.branchName }}</td>
      </template>
      <template v-slot:[`item.branchAddress`]="{ item }">
        <td class="px-4 py-3">{{ item.branchAddress?.addressLine1 || '-' }}</td>
      </template>
      <template v-slot:[`item.departmentName`]="{ item }">
        <td class="px-4 py-3">{{ getDepartment(item) || '-' }}</td>
      </template>
      <template v-slot:[`item.designationName`]="{ item }">
        <td class="px-4 py-3">{{ getDesignation(item) || '-' }}</td>
      </template>
      <template v-slot:[`item.commonLeavesCount`]="{ item }">
        <td class="px-4 py-3">
          <div class="d-flex item-center">
            {{ formatNumber(item.commonLeaves.length) }}
            <v-icon
              v-if="item.commonLeaves.length > 0"
              size="22"
              left
              color="primary"
              v-tooltip="{
                text: `${getLeaveTypesTooltip(item)}`,
                width: '300px',
                height: '400px',
                trigger: 'click',
                bottom: true
              }"
              class="ml-2 cursor-pointer"
            >
              mdi-information-variant-circle-outline
            </v-icon>
          </div>
        </td>
      </template>

      <template v-slot:[`item.actions`]="{ item }">
        <td class="px-4 py-3">
          <ActionButton
            :item="item"
            :can-edit="true"
            :can-delete="true"
            :show-edit-button="true"
            :show-delete-button="true"
            :handle-edit="editLeavePlan"
            :handle-delete="deleteLeavePlan"
          />
        </td>
      </template>
    </v-data-table>

    <v-dialog v-model="drawer" max-width="900px" persistent>
      <div class="flex flex-col h-[80vh] max-h-[80vh] bg-white">
        <!-- Fixed Header -->
        <div class="sticky top-0 z-10 bg-white">
          <v-card-title class="flex justify-between border-b-2 p-4">
            <h3 class="form-text">{{ currentPlanId ? 'Edit' : 'Add' }} Leave Plan</h3>
            <v-btn icon @click="closeDrawer">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
        </div>

        <!-- Scrollable Content -->
        <div class="flex-1 overflow-y-auto">
          <div class="p-4">
            <v-form ref="form">
              <h4 class="section-title mb-4">Basic Information</h4>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <v-text-field
                  label="Plan Name"
                  outlined
                  dense
                  hide-details
                  v-model="planName"
                  :rules="[mandatoryRule]"
                ></v-text-field>
                <v-select
                  v-model="planYear"
                  :items="yearOptions"
                  label="Select Year"
                  outlined
                  dense
                  hide-details
                  :disabled="currentPlanId"
                  :rules="[mandatoryRule]"
                ></v-select>
                <v-autocomplete
                  v-model="selectedBranch"
                  :items="branches"
                  label="Select Branch"
                  item-text="branchName"
                  item-value="_id"
                  outlined
                  dense
                  hide-details
                >
                  <template v-slot:item="{ item }">
                    <div>
                      <div>{{ item.branchName }}</div>
                      <div class="text--secondary caption">{{ item.address }}</div>
                    </div>
                  </template>
                </v-autocomplete>

                <v-autocomplete
                  v-model="selectedDepartment"
                  :items="departments"
                  label="Select Department"
                  item-text="departmentName"
                  item-value="_id"
                  outlined
                  dense
                  hide-details
                  @change="fetchDesignationsByDepartment(selectedDepartment)"
                >
                </v-autocomplete
                ><v-autocomplete
                  v-model="selectedDesignation"
                  :items="designations"
                  label="Select Designation"
                  item-text="designationName"
                  item-value="_id"
                  outlined
                  dense
                  hide-details
                >
                </v-autocomplete>
              </div>

              <!-- <h4 class="section-title mb-4 mt-6">Common Leave Policy</h4>
              <div class="border border-gray-300 p-4 rounded">
                <v-textarea
                  label="Policy Description"
                  outlined
                  dense
                  hide-details
                  v-model="commonLeavePolicy.description"
                  rows="2"
                ></v-textarea>
                <v-text-field
                  label="Supporting Documents (comma separated)"
                  outlined
                  dense
                  hide-details
                  v-model="supportingDocuments"
                  class="mt-2"
                ></v-text-field>
              </div> -->

              <h4 class="section-title mb-4 mt-6">Leave Types</h4>
              <div class="border border-gray-300 rounded">
                <div class="flex justify-end items-center p-3 bg-gray-50">
                  <v-btn
                    x-small
                    color="primary"
                    outlined
                    @click="addLeaveType"
                    class="normal-case font-medium text-xs"
                    :disabled="allLeaveTypesUsed"
                    v-tooltip="{
                      text: allLeaveTypesUsed ? 'All leave types have been added' : '',
                      disabled: !allLeaveTypesUsed
                    }"
                  >
                    <v-icon x-small class="mr-1">mdi-plus</v-icon>
                    Add
                  </v-btn>
                </div>

                <div class="p-3">
                  <div
                    v-for="(leave, index) in commonLeaves"
                    :key="index"
                    class="border-b border-gray-200 pb-4 mb-4 last:border-0 last:pb-0 last:mb-0 relative"
                  >
                    <!-- Clear button positioned at top-right corner -->
                    <div class="absolute top-[-10px] right-[-10px] mt-2 mr-2">
                      <v-btn
                        icon
                        x-small
                        color="error"
                        @click="removeLeaveType(index)"
                        v-if="commonLeaves.length > 1"
                      >
                        <v-icon small>mdi-close</v-icon>
                      </v-btn>
                    </div>

                    <div
                      class="grid grid-cols-1 md:grid-cols-12 gap-3 items-center pt-4 bg-gray-50 p-2 rounded border border-gray-200"
                    >
                      <div class="md:col-span-4">
                        <v-select
                          v-model="leave.typeOfLeave"
                          :items="availableLeaveTypes(index)"
                          label="Type"
                          outlined
                          dense
                          hide-details
                          @change="updateLeaveTypeDetails(index)"
                          class="text-sm"
                        ></v-select>
                      </div>
                      <!-- <div class="md:col-span-4" v-if="leave.typeOfLeave === 'CUSTOM_LEAVE'">
                        <v-text-field
                          v-model="leave.customName"
                          label="Name"
                          outlined
                          dense
                          hide-details
                          :rules="[mandatoryRule]"
                        ></v-text-field>
                      </div> -->
                      <div class="md:col-span-4">
                        <v-text-field
                          v-model="leave.customName"
                          label="Name"
                          outlined
                          dense
                          hide-details
                          class="text-sm"
                        ></v-text-field>
                      </div>
                      <div class="md:col-span-4">
                        <v-text-field
                          v-model="leave.shortName"
                          label="Short Name"
                          outlined
                          dense
                          hide-details
                          class="text-sm"
                          readonly
                        ></v-text-field>
                      </div>

                      <div class="md:col-span-4">
                        <v-text-field
                          v-model.number="leave.numberOfDays"
                          label="Days Quota"
                          type="number"
                          outlined
                          dense
                          hide-details
                          :rules="[mandatoryRule]"
                          class="text-sm"
                        ></v-text-field>
                      </div>
                      <div class="md:col-span-4">
                        <v-text-field
                          v-model.number="leave.min_notice_period"
                          label="Min Notice (Days)"
                          type="number"
                          outlined
                          dense
                          hide-details
                          class="text-sm"
                        ></v-text-field>
                      </div>

                      <div class="md:col-span-4">
                        <v-text-field
                          v-model.number="leave.max_consecutive_days"
                          label="Max Consecutive (Days)"
                          type="number"
                          outlined
                          dense
                          hide-details
                          class="text-sm"
                        ></v-text-field>
                      </div>

                      <div class="md:col-span-12">
                        <v-textarea
                          v-model="leave.description"
                          label="Description"
                          outlined
                          dense
                          hide-details
                          rows="2"
                          class="text-sm"
                        ></v-textarea>
                      </div>

                      <!-- Additional Leave Type Fields -->
                      <div
                        v-if="leave.typeOfLeave && leave.typeOfLeave !== 'CUSTOM_LEAVE'"
                        class="md:col-span-12 mt-2"
                      >
                        <div>
                          <v-divider class="mb-2"></v-divider>
                          <h4 class="section-title">Eligibility</h4>
                          <!-- Leave Type Section -->
                          <div class="mb-4">
                            <div class="grid grid-cols-1 md:grid-cols-3 gap-2">
                              <v-select
                                v-model="leave.eligible_for"
                                :items="[
                                  { text: 'Both', value: 'Both' },
                                  { text: 'Male', value: 'Male' },
                                  { text: 'Female', value: 'Female' }
                                ]"
                                label="Gender"
                                outlined
                                dense
                                hide-details
                                class="text-sm"
                              ></v-select>
                              <v-select
                                v-model="leave.marriage_status"
                                :items="['Any', 'Married', 'Unmarried']"
                                label="Marriage Status"
                                outlined
                                dense
                                hide-details
                                class="text-sm"
                              ></v-select>
                              <v-select
                                v-model="leave.approval_level"
                                :items="['immediate_supervisor', 'manager', 'hr', 'admin']"
                                label="Approval Level"
                                outlined
                                dense
                                hide-details
                                class="text-sm"
                              ></v-select>
                            </div>
                          </div>

                          <!-- Divider -->
                          <v-divider class="mb-2"></v-divider>

                          <!-- Carry Forward Section -->
                          <div class="mb-2">
                            <div class="flex items-center">
                              <v-checkbox
                                v-model="leave.is_carry_forwardable"
                                label="Carry Forwardable"
                                dense
                                hide-details
                                class="text-sm mt-0"
                              ></v-checkbox>
                            </div>
                            <div class="mt-2">
                              <div class="grid grid-cols-1 md:grid-cols-3 gap-2">
                                <v-text-field
                                  v-model.number="leave.max_carry_forward_days"
                                  label="Max Carry Forward Days"
                                  type="number"
                                  outlined
                                  dense
                                  hide-details
                                  class="text-sm"
                                ></v-text-field>
                                <v-text-field
                                  v-model.number="leave.carry_forward_percentage"
                                  label="Carry Forward %"
                                  type="number"
                                  outlined
                                  dense
                                  hide-details
                                  class="text-sm"
                                ></v-text-field>
                                <v-text-field
                                  v-model.number="leave.expiry_months"
                                  label="Expiry Months"
                                  type="number"
                                  outlined
                                  dense
                                  hide-details
                                  class="text-sm"
                                ></v-text-field>
                              </div>
                            </div>
                          </div>

                          <!-- Divider -->
                          <v-divider class="mb-2"></v-divider>

                          <!-- Deduction Section -->
                          <div>
                            <div class="flex items-center">
                              <v-checkbox
                                v-model="leave.can_be_deducted"
                                label="Deductable"
                                dense
                                hide-details
                                class="text-xs mt-0"
                              ></v-checkbox>
                            </div>
                            <div class="mt-2">
                              <div class="grid grid-cols-1 md:grid-cols-2 gap-2">
                                <v-text-field
                                  v-model.number="leave.deduction_priority"
                                  label="Priority Order"
                                  type="number"
                                  outlined
                                  dense
                                  hide-details
                                  class="text-sm"
                                ></v-text-field>
                              </div>
                            </div>
                            <v-divider class="my-2"></v-divider>

                            <div class="flex items-center mt-2">
                              <v-checkbox
                                v-model="leave.requires_documentation"
                                label="Requires Documentation"
                                dense
                                hide-details
                                class="text-xs mt-0"
                              ></v-checkbox>
                            </div>

                            <!-- <div class="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
                              
                            </div> -->
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </v-form>
          </div>
        </div>

        <!-- Fixed Footer -->
        <div class="sticky bottom-0 z-10 bg-white">
          <v-divider></v-divider>
          <div class="flex justify-end p-4">
            <v-btn class="normal-case" text @click="closeDrawer">Cancel</v-btn>
            <v-btn color="primary" class="normal-case" @click="saveLeavePlan">
              <v-icon left>mdi-content-save</v-icon>
              Save</v-btn
            >
          </div>
        </div>
      </div>
    </v-dialog>

    <AlertPopUp
      :showConfirmationDialog="showConfirmationDialog"
      :confirmationTitle="'Confirm Delete'"
      :confirmationMessage="'Are you sure you want to delete the leave plan?'"
      :confirmationActionText="'Delete'"
      :confirmationActionColor="'red'"
      :performAction="confirmDelete"
      :cancel="cancelDelete"
    />
  </div>
</template>

<script>
import { convertToTitleCase } from '@/utils/common'
import AlertPopUp from '@/components/AlertPopUp'
import ActionButton from '@/components/ActionButton.vue'
import { axiosUrl } from '@/utils/workforceApi'

export default {
  name: 'LeaveManagement',
  components: {
    AlertPopUp,
    ActionButton
  },
  props: {
    branches: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      drawer: false,
      planName: '',
      planYear: new Date().getFullYear(),
      yearOptions: this.generateYearOptions(),
      selectedBranch: null,
      commonLeavePolicy: {
        description: '',
        supportingDocuments: []
      },
      supportingDocuments: '',
      commonLeaves: [
        {
          typeOfLeave: 'CASUAL_LEAVE',
          customName: 'Casual Leave',
          numberOfDays: 12,
          policyDescription: {
            description: 'Short-term personal work, emergencies, or unplanned activities'
          },
          // Predefined leave type fields
          displayName: 'Casual Leave',
          shortName: 'CL',
          description: 'Short-term personal work, emergencies, or unplanned activities',
          eligible_for: 'Both',
          default_days_quota: 12,
          is_carry_forwardable: true,
          max_carry_forward_days: 6,
          carry_forward_percentage: 50,
          expiry_months: 3,
          marriage_status: 'Any',
          min_notice_period: 0,
          max_consecutive_days: 5,
          requires_documentation: false,
          can_be_deducted: true,
          deduction_priority: 1,
          approval_level: 'immediate_supervisor'
        }
      ],
      currentPlanId: null,
      showConfirmationDialog: false,
      leavePlans: [],
      loading: true,
      templateToDelete: null,
      tableHeaders: [
        { text: 'Plan', value: 'planName' },
        // { text: 'Plan Year', value: 'planYear' },
        { text: 'Department', value: 'departmentName' },
        { text: 'Designation', value: 'designationName' },
        { text: 'Branch', value: 'branchName' },
        { text: 'Address', value: 'branchAddress' },
        { text: 'Leave Types', value: 'commonLeavesCount' },
        { text: 'Actions', value: 'actions' }
      ],
      predefinedLeaveTypes: [],
      departments: [],
      designations: [],
      selectedDepartment: null,
      selectedDesignation: null
    }
  },
  computed: {
    // Derive leaveTypes from predefinedLeaveTypes
    leaveTypes() {
      if (!this.predefinedLeaveTypes || !Array.isArray(this.predefinedLeaveTypes)) {
        return []
      }
      return this.predefinedLeaveTypes.map((leaveType) => leaveType.type)
    },
    // Filter out already used leave types for each specific dropdown
    availableLeaveTypes() {
      if (!this.predefinedLeaveTypes || !Array.isArray(this.predefinedLeaveTypes)) {
        return []
      }

      // Return a function that takes an index and returns available types for that specific dropdown
      return (index) => {
        // Get currently selected leave types from OTHER dropdowns (not the current one)
        const usedTypes = this.commonLeaves
          .filter(
            (leave, idx) =>
              idx !== index && leave.typeOfLeave && leave.typeOfLeave !== 'CUSTOM_LEAVE'
          )
          .map((leave) => leave.typeOfLeave)

        // Always include the currently selected type for this dropdown
        const currentType = this.commonLeaves[index]?.typeOfLeave

        // Filter out used types from predefined leave types, but always include current type
        return this.predefinedLeaveTypes
          .filter(
            (leaveType) => !usedTypes.includes(leaveType.type) || leaveType.type === currentType
          )
          .map((leaveType) => leaveType.type)
      }
    },
    // Check if all leave types are used
    allLeaveTypesUsed() {
      // Get all predefined leave types except CUSTOM_LEAVE
      const allPredefinedTypes = this.predefinedLeaveTypes
        .filter((leaveType) => leaveType.type !== 'CUSTOM_LEAVE')
        .map((leaveType) => leaveType.type)

      // Get currently used types
      const usedTypes = this.commonLeaves
        .map((leave) => leave.typeOfLeave)
        .filter((type) => type && type !== 'CUSTOM_LEAVE')

      // Check if all predefined types are used
      return allPredefinedTypes.length > 0 && usedTypes.length >= allPredefinedTypes.length
    },
    mandatoryRule: () => (value) => !!value || 'This field is required'
  },
  methods: {
    generateYearOptions() {
      const currentYear = this.$dayjs().year()
      const years = []
      for (let year = currentYear; year <= currentYear + 1; year++) {
        years.push({ text: year.toString(), value: year })
      }
      return years
    },
    getDesignation(item) {
      const designation = this.designations.find((d) => d._id === item.designation)
      return designation?.designationName || '-'
    },
    getDepartment(item) {
      const department = this.departments.find((d) => d._id === item.department)
      return department?.departmentName || '-'
    },
    async fetchDepartments() {
      try {
        const response = await this.$axios.get(axiosUrl.departments.getDepartments)
        this.departments = response.data.departments || []
      } catch (error) {
        console.error('Error fetching departments:', error)
        this.departments = []
      }
    },
    async fetchDesignationsByDepartment(department) {
      try {
        const url = department
          ? `${axiosUrl.designations.getDesignations}?department=${department}`
          : axiosUrl.designations.getDesignations
        const response = await this.$axios.get(url)
        this.designations = response.data.designations || []
      } catch (error) {
        console.error('Error fetching designations:', error)
        this.designations = []
      }
    },
    titleCase(str) {
      return convertToTitleCase(str)
    },
    getBranchNameById(branchId) {
      if (!branchId || !this.branches || this.branches.length === 0) return null
      const branch = this.branches.find((b) => b._id === branchId)
      return branch ? branch.branchName : null
    },
    // Update leave type details when a new type is selected
    updateLeaveTypeDetails(index) {
      const leave = this.commonLeaves[index]
      const type = leave.typeOfLeave

      if (type === 'CUSTOM_LEAVE') {
        // Reset custom leave fields
        leave.customName = ''
        leave.displayName = ''
        leave.shortName = ''
        leave.description = ''
        leave.eligible_for = 'Both'
        leave.default_days_quota = 0
        leave.numberOfDays = 0
        leave.is_carry_forwardable = false
        leave.max_carry_forward_days = 0
        leave.carry_forward_percentage = 0
        leave.expiry_months = 0
        leave.marriage_status = 'Any'
        leave.min_notice_period = 0
        leave.max_consecutive_days = 1
        leave.requires_documentation = false
        leave.can_be_deducted = true
        leave.deduction_priority = 0
        leave.approval_level = 'immediate_supervisor'
      } else {
        // Find the predefined leave type by type
        const predefined = this.predefinedLeaveTypes.find((lt) => lt.type === type)
        if (predefined) {
          // Populate with predefined values
          Object.keys(predefined).forEach((key) => {
            // Skip the 'type' field as it's already set
            if (key !== 'type') {
              this.$set(leave, key, predefined[key])
            }
          })
          // Set customName to displayName for predefined leave types
          leave.customName = predefined.displayName || ''
          // Set numberOfDays to default_days_quota
          leave.numberOfDays = predefined.default_days_quota || 0
        }
      }
    },
    formatNumber(number) {
      return number < 10 ? `0${number}` : number
    },
    addLeaveType() {
      // Check if all leave types are already used
      if (this.allLeaveTypesUsed) {
        this.$toast.error('All leave types have already been added')
        return
      }

      // Add a new leave type with the first available predefined leave type
      // or default to CUSTOM_LEAVE if no predefined types are available
      let defaultType = 'CUSTOM_LEAVE'
      let defaultLeave = {
        typeOfLeave: defaultType,
        customName: '',
        numberOfDays: 0,
        description: ''
      }

      // Get available types for the new leave (index will be the last one)
      const availableTypes = this.availableLeaveTypes(this.commonLeaves.length)

      // If there are available predefined leave types, use the first one
      if (availableTypes.length > 0 && availableTypes[0] !== 'CUSTOM_LEAVE') {
        defaultType = availableTypes[0]
        defaultLeave.typeOfLeave = defaultType

        // Add predefined leave type fields if available
        const predefined = this.predefinedLeaveTypes.find((lt) => lt.type === defaultType)
        if (predefined) {
          Object.keys(predefined).forEach((key) => {
            if (key !== 'type') {
              defaultLeave[key] = predefined[key]
            }
          })
          defaultLeave.customName = predefined.displayName
          defaultLeave.numberOfDays = predefined.default_days_quota || 0
        }
      } else if (this.predefinedLeaveTypes.length > 0) {
        // Fallback to casual leave if available
        const predefined = this.predefinedLeaveTypes.find((lt) => lt.type === 'CASUAL_LEAVE')
        if (predefined) {
          defaultType = 'CASUAL_LEAVE'
          defaultLeave.typeOfLeave = defaultType
          Object.keys(predefined).forEach((key) => {
            if (key !== 'type') {
              defaultLeave[key] = predefined[key]
            }
          })
          defaultLeave.customName = predefined.displayName
          defaultLeave.numberOfDays = predefined.default_days_quota || 12
        }
      } else {
        // Fallback to default values
        defaultLeave.customName = 'Custom Leave'
        defaultLeave.numberOfDays = 0
        defaultLeave.description = 'Custom leave type'
        defaultLeave.shortName = 'CL'
        defaultLeave.eligible_for = 'Both'
        defaultLeave.default_days_quota = 0
        defaultLeave.is_carry_forwardable = false
        defaultLeave.max_carry_forward_days = 0
        defaultLeave.carry_forward_percentage = 0
        defaultLeave.expiry_months = 0
        defaultLeave.marriage_status = 'Any'
        defaultLeave.min_notice_period = 0
        defaultLeave.max_consecutive_days = 1
        defaultLeave.requires_documentation = false
        defaultLeave.can_be_deducted = true
        defaultLeave.deduction_priority = 0
        defaultLeave.approval_level = 'immediate_supervisor'
      }

      this.commonLeaves.push(defaultLeave)
    },
    removeLeaveType(index) {
      this.commonLeaves.splice(index, 1)
    },
    openCreateLeavePlan() {
      this.resetForm()
      this.planYear = new Date().getFullYear()
      this.$nextTick(() => {
        if (this.branches && this.branches.length > 0) {
          this.selectedBranch = this.branches[0]._id
        }
        if (this.departments && this.departments.length > 0) {
          this.selectedDepartment = this.departments[0]._id
          this.fetchDesignationsByDepartment(this.selectedDepartment)
        }
        if (this.designations && this.designations.length > 0) {
          this.selectedDesignation = this.designations[0]._id
        }
      })
      this.drawer = true
    },
    closeDrawer() {
      this.drawer = false
      this.resetForm()
    },
    resetForm() {
      this.planName = ''
      this.planYear = new Date().getFullYear()
      this.selectedBranch = null
      this.commonLeavePolicy = {
        description: '',
        supportingDocuments: []
      }
      this.supportingDocuments = ''

      // Reset to default leave type using available types logic
      let defaultType = 'CUSTOM_LEAVE'
      let defaultLeave = {
        typeOfLeave: defaultType,
        customName: '',
        numberOfDays: 0,
        policyDescription: {
          description: 'Short-term personal work, emergencies, or unplanned activities'
        }
      }

      // If there are predefined leave types, use the first available one
      if (this.predefinedLeaveTypes && this.predefinedLeaveTypes.length > 0) {
        // Get available types for index 0
        const availableTypes = this.availableLeaveTypes(0)

        // Use the first available type if it's not CUSTOM_LEAVE
        const predefined =
          availableTypes.length > 0 && availableTypes[0] !== 'CUSTOM_LEAVE'
            ? this.predefinedLeaveTypes.find((lt) => lt.type === availableTypes[0])
            : this.predefinedLeaveTypes.find((lt) => lt.type === 'CASUAL_LEAVE') ||
              this.predefinedLeaveTypes[0]

        if (predefined) {
          defaultType = predefined.type
          defaultLeave.typeOfLeave = defaultType
          Object.keys(predefined).forEach((key) => {
            if (key !== 'type') {
              defaultLeave[key] = predefined[key]
            }
          })
          defaultLeave.customName = predefined.displayName || 'Casual Leave'
          // Set numberOfDays to default_days_quota
          defaultLeave.numberOfDays = predefined.default_days_quota || 12
        }
      } else {
        defaultLeave.shortName = 'CL'
        defaultLeave.description = 'Short-term personal work, emergencies, or unplanned activities'
        defaultLeave.eligible_for = 'Both'
        defaultLeave.default_days_quota = 12
        defaultLeave.is_carry_forwardable = true
        defaultLeave.max_carry_forward_days = 6
        defaultLeave.carry_forward_percentage = 50
        defaultLeave.expiry_months = 3
        defaultLeave.marriage_status = 'Any'
        defaultLeave.min_notice_period = 0
        defaultLeave.max_consecutive_days = 5
        defaultLeave.requires_documentation = false
        defaultLeave.can_be_deducted = true
        defaultLeave.deduction_priority = 1
        defaultLeave.approval_level = 'immediate_supervisor'
      }

      this.commonLeaves = [defaultLeave]
      this.currentPlanId = null
      if (this.$refs.form) {
        this.$refs.form.reset()
      }
    },
    async fetchLeavePlans() {
      this.loading = true
      try {
        const response = await this.$axios.get('/orgSetup/org-leaves')
        this.leavePlans = response.data?.leaves || []

        // Process leave plans to include branch and role names
        this.leavePlans = this.leavePlans.map((plan) => {
          return {
            ...plan,
            branchName: plan.branch?.branchName || '-',
            branchAddress: plan.branch?.branchLocation || '-'
          }
        })
      } catch (error) {
        console.error('Error fetching leave plans:', error)
        this.$toast.error('Failed to load leave plans')
        this.leavePlans = []
      } finally {
        this.loading = false
      }
    },
    async saveLeavePlan() {
      // Validate form
      if (!this.$refs.form.validate()) {
        return
      }

      // Validate required fields
      const requiredFields = [
        { field: this.planName, name: 'Plan Name' },
        { field: this.planYear, name: 'Plan Year' }
      ]

      for (let { field, name } of requiredFields) {
        if (!field) {
          this.$toast.error(`${name} is required`)
          return
        }
      }

      // Validate leave types
      for (let i = 0; i < this.commonLeaves.length; i++) {
        const leave = this.commonLeaves[i]
        if (
          leave.typeOfLeave === 'CUSTOM_LEAVE' &&
          (!leave.customName || leave.customName.trim() === '')
        ) {
          this.$toast.error(`Custom name is required for custom leave type at position ${i + 1}`)
          return
        }
      }

      // Process supporting documents
      const supportingDocs = this.supportingDocuments
        .split(',')
        .map((doc) => doc.trim())
        .filter((doc) => doc.length > 0)

      const payload = {
        planName: `${this.planName}`,
        planYear: Number(this.planYear),
        branch: this.selectedBranch,
        department: this.selectedDepartment,
        designation: this.selectedDesignation,
        commonLeavePolicy: {
          description: this.commonLeavePolicy.description,
          supportingDocuments: supportingDocs
        },
        commonLeaves: this.commonLeaves.map((leave) => {
          const leaveData = {
            numberOfDays: Number(leave.numberOfDays),
            description: leave.description
          }

          // Add customName only if it's a custom leave or if there's a custom name provided
          if (leave.typeOfLeave === 'CUSTOM_LEAVE') {
            leaveData.customName = leave.customName || null
          } else if (leave.customName && leave.customName.trim() !== '') {
            leaveData.customName = leave.customName
          }

          // Add predefined leave type fields
          if (leave.typeOfLeave !== 'CUSTOM_LEAVE') {
            leaveData.typeOfLeave = leave.typeOfLeave
            leaveData.shortName = leave.shortName
            leaveData.description = leave.description
            leaveData.eligible_for = leave.eligible_for
            leaveData.is_carry_forwardable = leave.is_carry_forwardable
            if (leave.is_carry_forwardable) {
              leaveData.max_carry_forward_days = leave.max_carry_forward_days
              leaveData.carry_forward_percentage = leave.carry_forward_percentage
            }
            leaveData.expiry_months = leave.expiry_months
            leaveData.marriage_status = leave.marriage_status
            leaveData.min_notice_period = leave.min_notice_period
            leaveData.max_consecutive_days = leave.max_consecutive_days
            leaveData.requires_documentation = leave.requires_documentation
            leaveData.can_be_deducted = leave.can_be_deducted
            if (leave.can_be_deducted) {
              leaveData.deduction_priority = leave.deduction_priority
            }
            leaveData.approval_level = leave.approval_level
          }

          return leaveData
        })
      }

      try {
        if (this.currentPlanId) {
          const response = await this.$axios.put(
            `/orgSetup/org-leaves/${this.currentPlanId}`,
            payload
          )
          this.$toast.success(response.data.message || 'Leave plan updated successfully')
        } else {
          const response = await this.$axios.post('/orgSetup/org-leaves', payload)
          this.$toast.success(response.data.message || 'Leave plan created successfully')
        }

        // Close drawer and refresh data
        this.closeDrawer()
        await this.fetchLeavePlans()
      } catch (error) {
        console.error('Error saving leave plan:', error)
        const errorMessage = error.response?.data?.message || 'Failed to save leave plan'
        this.$toast.error(errorMessage)
      }
    },
    editLeavePlan(plan) {
      this.currentPlanId = plan._id
      this.planName = plan.planName
      this.planYear = plan.planYear
      this.selectedBranch = plan.branch || null
      this.commonLeavePolicy = {
        description: plan.commonLeavePolicy?.description || '',
        supportingDocuments: plan.commonLeavePolicy?.supportingDocuments || []
      }
      this.supportingDocuments = plan.commonLeavePolicy?.supportingDocuments?.join(', ') || ''
      this.selectedDepartment = plan.department || null
      this.fetchDesignationsByDepartment(this.selectedDepartment)
      this.selectedDesignation = plan.designation || null
      if (plan.commonLeaves && Array.isArray(plan.commonLeaves)) {
        this.commonLeaves = plan.commonLeaves.map((leave) => {
          // Create a base leave object
          const leaveObj = {
            typeOfLeave: leave.typeOfLeave || 'CASUAL_LEAVE',
            customName: leave.customName,
            numberOfDays: leave.numberOfDays || leave.default_days_quota || 12,
            policyDescription: {
              description:
                leave.policyDescription?.description ||
                leave.description ||
                'Short-term personal work, emergencies, or unplanned activities'
            }
          }

          // Add predefined leave type fields if available
          if (leave.typeOfLeave && leave.typeOfLeave !== 'CUSTOM_LEAVE') {
            const predefined = this.predefinedLeaveTypes.find((lt) => lt.type === leave.typeOfLeave)
            if (predefined) {
              Object.keys(predefined).forEach((key) => {
                if (key !== 'type') {
                  leaveObj[key] = leave[key] !== undefined ? leave[key] : predefined[key]
                }
              })
              // Ensure customName is set to displayName for predefined leave types
              if (!leaveObj.customName && predefined.displayName) {
                leaveObj.customName = predefined.displayName
              }
              // Ensure numberOfDays is set to default_days_quota if not already set
              if (
                (!leaveObj.numberOfDays || leaveObj.numberOfDays === 0) &&
                predefined.default_days_quota
              ) {
                leaveObj.numberOfDays = predefined.default_days_quota
              }
            } else {
              leaveObj.shortName = leave.shortName || 'CL'
              leaveObj.description =
                leave.description ||
                'Short-term personal work, emergencies, or unplanned activities'
              leaveObj.eligible_for = leave.eligible_for || 'Both'
              leaveObj.default_days_quota = leave.default_days_quota || 12
              leaveObj.is_carry_forwardable = leave.is_carry_forwardable || false
              leaveObj.max_carry_forward_days = leave.max_carry_forward_days || 0
              leaveObj.carry_forward_percentage = leave.carry_forward_percentage || 0
              leaveObj.expiry_months = leave.expiry_months || 0
              leaveObj.marriage_status = leave.marriage_status || 'Any'
              leaveObj.min_notice_period = leave.min_notice_period || 0
              leaveObj.max_consecutive_days = leave.max_consecutive_days || 5
              leaveObj.requires_documentation = leave.requires_documentation || false
              leaveObj.can_be_deducted =
                leave.can_be_deducted !== undefined ? leave.can_be_deducted : true
              leaveObj.deduction_priority = leave.deduction_priority || 0
              leaveObj.approval_level = leave.approval_level || 'immediate_supervisor'
              leaveObj.customName = leaveObj.displayName

              // Ensure numberOfDays is set
              if (!leaveObj.numberOfDays || leaveObj.numberOfDays === 0) {
                leaveObj.numberOfDays = leaveObj.default_days_quota || 12
              }
            }
          } else if (leave.typeOfLeave === 'CUSTOM_LEAVE') {
            // For custom leave, use existing values or defaults
            leaveObj.displayName = leave.displayName || ''
            leaveObj.shortName = leave.shortName || ''
            leaveObj.description = leave.description || ''
            leaveObj.eligible_for = leave.eligible_for || 'Both'
            leaveObj.default_days_quota = leave.default_days_quota || 0
            leaveObj.is_carry_forwardable = leave.is_carry_forwardable || false
            leaveObj.max_carry_forward_days = leave.max_carry_forward_days || 0
            leaveObj.carry_forward_percentage = leave.carry_forward_percentage || 0
            leaveObj.expiry_months = leave.expiry_months || 0
            leaveObj.marriage_status = leave.marriage_status || 'Any'
            leaveObj.min_notice_period = leave.min_notice_period || 0
            leaveObj.max_consecutive_days = leave.max_consecutive_days || 1
            leaveObj.requires_documentation = leave.requires_documentation || false
            leaveObj.can_be_deducted =
              leave.can_be_deducted !== undefined ? leave.can_be_deducted : true
            leaveObj.deduction_priority = leave.deduction_priority || 0
            leaveObj.approval_level = leave.approval_level || 'immediate_supervisor'
            // Ensure customName is set
            if (!leaveObj.customName) {
              leaveObj.customName = ''
            }
            // Ensure numberOfDays is set
            if (!leaveObj.numberOfDays) {
              leaveObj.numberOfDays = leaveObj.default_days_quota || 0
            }
          }

          return leaveObj
        })
      } else {
        // Reset to default leave type
        let defaultType = 'CUSTOM_LEAVE'
        let defaultLeave = {
          typeOfLeave: defaultType,
          customName: '',
          numberOfDays: 0,
          policyDescription: {
            description: 'Short-term personal work, emergencies, or unplanned activities'
          }
        }

        // If there are predefined leave types, use the first available one
        if (this.predefinedLeaveTypes && this.predefinedLeaveTypes.length > 0) {
          const predefined =
            this.predefinedLeaveTypes.find((lt) => lt.type === 'CASUAL_LEAVE') ||
            this.predefinedLeaveTypes[0]
          if (predefined) {
            defaultType = predefined.type
            defaultLeave.typeOfLeave = defaultType
            Object.keys(predefined).forEach((key) => {
              if (key !== 'type') {
                defaultLeave[key] = predefined[key]
              }
            })
            defaultLeave.customName = predefined.displayName || 'Casual Leave'
            // Set numberOfDays to default_days_quota
            defaultLeave.numberOfDays = predefined.default_days_quota || 12
          }
        } else {
          defaultLeave.shortName = 'CL'
          defaultLeave.description =
            'Short-term personal work, emergencies, or unplanned activities'
          defaultLeave.eligible_for = 'Both'
          defaultLeave.default_days_quota = 12
          defaultLeave.is_carry_forwardable = true
          defaultLeave.max_carry_forward_days = 6
          defaultLeave.carry_forward_percentage = 50
          defaultLeave.expiry_months = 3
          defaultLeave.marriage_status = 'Any'
          defaultLeave.min_notice_period = 0
          defaultLeave.max_consecutive_days = 5
          defaultLeave.requires_documentation = false
          defaultLeave.can_be_deducted = true
          defaultLeave.deduction_priority = 1
          defaultLeave.approval_level = 'immediate_supervisor'
        }

        this.commonLeaves = [defaultLeave]
      }

      this.drawer = true
    },
    deleteLeavePlan(plan) {
      this.templateToDelete = plan
      this.showConfirmationDialog = true
    },
    async confirmDelete() {
      if (!this.templateToDelete) return

      try {
        const response = await this.$axios.delete(
          `/orgSetup/org-leaves/${this.templateToDelete._id}`
        )
        this.$toast.success(response.data.message || 'Leave plan deleted successfully')
        await this.fetchLeavePlans()
      } catch (error) {
        console.error('Error deleting leave plan:', error)
        const errorMessage = error.response?.data?.message || 'Failed to delete leave plan'
        this.$toast.error(errorMessage)
      } finally {
        this.cancelDelete()
        this.templateToDelete = null
      }
    },
    async getStaticLeaveTypes() {
      try {
        const response = await this.$axios.get('/orgSetup/org-leaves/leave-types')
        this.predefinedLeaveTypes = response.data?.leaveTypes || []
      } catch (error) {
        console.log(error)
        this.predefinedLeaveTypes = []
      }
    },
    getLeaveTypesTooltip(item) {
      if (!item.commonLeaves || item.commonLeaves.length === 0) return 'No leave types'

      return item.commonLeaves
        .map(
          (leave, index) =>
            `<div style="text-align:left; margin-bottom: 8px; padding: 4px 0; border-bottom: 1px solid rgba(200,250,255,0.2);">
          <strong>${index + 1}. ${leave.customName || leave.typeOfLeave} (${
              leave.numberOfDays
            } days )</strong>

        </div>`
        )
        .join('')
    },
    cancelDelete() {
      this.showConfirmationDialog = false
      this.templateToDelete = null
    }
  },
  async mounted() {
    await this.fetchLeavePlans()
    await this.getStaticLeaveTypes()
    await this.fetchDepartments()
    await this.fetchDesignationsByDepartment()
  }
}
</script>

<style scoped>
.form-text {
  font-size: 1.25rem;
  padding: 0px;
  font-weight: 600;
  color: #21427d;
  margin: 0;
}
.section-title {
  font-size: 1rem;
  font-weight: 500;
  color: #21427d;
  margin-bottom: 10px;
  align-items: center;
}
.font-title {
  font-size: 0.875rem;
  color: #000;
  margin-bottom: 0px !important;
  max-width: 120px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
/* Compact styling for form fields */
.text-xs ::v-deep .v-input__control {
  min-height: 36px !important;
}
.text-xs ::v-deep .v-input__slot {
  min-height: 36px !important;
}
.table-radius {
  border-radius: 0px !important;
}
.text-xs ::v-deep .v-text-field__details {
  min-height: 16px !important;
}
.text-xs ::v-deep .v-input__append-inner,
.text-xs ::v-deep .v-input__prepend-inner {
  margin-top: 6px !important;
}
.v-data-table >>> .v-data-table__checkbox {
  padding-left: 5px;
}
.v-data-table >>> .v-data-table__wrapper > table > tbody > tr > td > .v-data-table__checkbox {
  text-align: center;
}
</style>
